import type { Command } from '../components/primitives';

/**
 * Per-surface Brain Bar (⌘K) default commands.
 *
 * Pages register their own commands via `usePageCommands(...)` from
 * BrainBarContext. AppShell merges page-registered commands with the
 * always-on shell commands here.
 *
 * Each command has a `family` ('do' | 'go' | 'ask') and an `onRun`
 * provided at render time by the host (so they can close over
 * `navigate()` and Brain `send()` callbacks).
 */

export interface ShellCommandSpec {
  family: 'do' | 'go' | 'ask';
  label: string;
  hint?: string;
  /** Route to navigate to. GO commands; some DO commands also navigate. */
  route?: string;
  /** Custom window event to dispatch — used by panels that listen for
   *  global toggles (e.g. NowSurface). */
  event?: string;
}

/** Always-on commands the shell registers. Page commands take precedence —
 *  e.g. Home's HOME_COMMANDS also exposes "plan tonight" but routes it
 *  through Brain instead of just navigating, so we don't list it here.
 *
 *  Sprint 1 IA reshape (2026-05-07): with the sidebar collapsed to Today
 *  / Calendar / Reviews + Brain button + Settings foot-link, the
 *  CommandBar is now the primary discovery surface for every other page.
 *  Every non-pinned destination MUST live here as a `go` command. */
export const SHELL_COMMANDS: ShellCommandSpec[] = [
  { family: 'go', label: 'today',           hint: '/',                       route: '/' },
  { family: 'do', label: "what's ready",    hint: 'today panel',             event: 'now-surface:open' },
  { family: 'go', label: 'open this week',  hint: '/calendar',               route: '/calendar' },
  { family: 'go', label: 'sessions',        hint: '/sessions',               route: '/sessions' },
  { family: 'go', label: 'new session',     hint: '/sessions/new',           route: '/sessions/new' },
  { family: 'go', label: 'find a drill',    hint: '/drills',                 route: '/drills' },
  { family: 'go', label: 'new drill',       hint: '/drills/new',             route: '/drills/new' },
  { family: 'go', label: 'players',         hint: '/players',                route: '/players' },
  { family: 'go', label: 'games',           hint: '/games',                  route: '/games' },
  { family: 'go', label: 'principles',      hint: '/principles',             route: '/principles' },
  { family: 'go', label: 'curriculum',      hint: '/curriculum',             route: '/curriculum' },
  { family: 'go', label: 'clips',           hint: '/clips',                  route: '/clips' },
  { family: 'go', label: '3D pitch',        hint: '/3d',                     route: '/3d' },
  { family: 'go', label: 'set pieces',      hint: '/setpieces',              route: '/setpieces' },
  { family: 'go', label: 'reviews',         hint: '/reviews',                route: '/reviews' },
  { family: 'go', label: 'memories',        hint: '/memories',               route: '/memories' },
  { family: 'go', label: 'settings',        hint: '/settings',               route: '/settings' },
  { family: 'go', label: 'calibration',     hint: '/settings/calibration',   route: '/settings/calibration' },
  { family: 'go', label: 'onboarding',      hint: '/onboarding',             route: '/onboarding' },
  { family: 'do', label: 'start a coach check-in', hint: '/reviews/check-in', route: '/reviews/check-in' },
];

/**
 * Resolve a list of shell command specs into runnable Commands by binding
 * each `route` to the host's navigate function.
 */
export function resolveShellCommands(
  specs: ShellCommandSpec[],
  navigate: (to: string) => void,
): Command[] {
  return specs.map((spec) => ({
    family: spec.family,
    label: spec.label,
    hint: spec.hint,
    onRun: spec.route
      ? () => navigate(spec.route!)
      : spec.event
        ? () => window.dispatchEvent(new CustomEvent(spec.event!))
        : undefined,
  }));
}

/**
 * Per-page command sets. These are returned to pages so they can
 * register them via `usePageCommands(...)`.
 */

export interface PageCommandFactory {
  /** Page-level DO/ASK commands; the page hooks them up to its own callbacks. */
  build: (ctx: { send: (text: string) => void; isAcademy: boolean }) => Command[];
}

export const HOME_COMMANDS: PageCommandFactory = {
  build: ({ send, isAcademy }) => {
    const novice: Command[] = [
      { family: 'do',  label: 'plan tonight',     hint: 'Brain', onRun: () => send("Plan tonight's session.") },
      { family: 'do',  label: 'tag a thought',    hint: 'Brain', onRun: () => send('I want to tag a thought about today.') },
      { family: 'ask', label: 'what is working?', onRun: () => send('What is working with my team right now?') },
    ];
    const academyExtra: Command[] = [
      { family: 'do',  label: 'generate tactical brief', hint: 'Brain', onRun: () => send('Generate a tactical brief for our next opponent.') },
      { family: 'ask', label: 'why is this MD-3?',       onRun: () => send('Why is this MD-3 in the current cycle?') },
      { family: 'ask', label: 'compare to last MD',      onRun: () => send("Compare this week's plan to last MD.") },
    ];
    return isAcademy ? [...novice, ...academyExtra] : novice;
  },
};
